import { makeStyles } from '@material-ui/styles'
import Text from 'core/elements/Text'
import HintTooltip from 'core/elements/tooltip/HintTooltip'
import Theme from 'core/themes/model'

export interface StepOption {
  value: string
  label: string
}

export interface StepDetail {
  description: string
  timeEstimate?: string
}

interface Props {
  options: StepOption[]
  details?: Record<string, StepDetail>
  value: string
  onChange: (value: string) => void
  label?: string
  tooltip?: string
  subtitle?: string
  className?: string
}

const useStyles = makeStyles<Theme>((theme) => ({
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  sliderWrapper: {
    position: 'relative',
    paddingBottom: theme.spacing(3),
    marginTop: theme.spacing(2),
  },
  track: {
    position: 'absolute',
    top: 10,
    left: 'calc(100% / 6)',
    right: 'calc(100% / 6)',
    height: 4,
    backgroundColor: theme.palette.grey[200],
    transform: 'translateY(-50%)',
    zIndex: 0,
  },
  dots: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    position: 'relative',
    zIndex: 1,
  },
  dotButton: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 4,
      borderRadius: 4,
    },
  },
  dot: {
    width: 20,
    height: 20,
    borderRadius: '50%',
    border: `2px solid ${theme.palette.grey[200]}`,
    backgroundColor: theme.palette.common.white,
    boxSizing: 'border-box',
    transition: 'background-color 0.15s, border-color 0.15s',
  },
  dotActive: {
    border: `4px solid ${theme.palette.primary.main}`,
  },
  labelActive: {
    color: theme.palette.primary.main,
    fontWeight: 900,
  },
  descriptionBox: {
    border: `1px solid ${theme.palette.grey[200]}`,
    backgroundColor: theme.palette.common.white,
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(2),
    display: 'grid',
    gap: theme.spacing(1),
  },
  timeChip: {
    display: 'inline-block',
    backgroundColor: theme.palette.blue[150],
    borderRadius: theme.spacing(2),
    padding: theme.spacing(0.5, 1),
    width: 'fit-content',
  },
}))

export default function StepSlider({
  options,
  details,
  value,
  onChange,
  label,
  tooltip,
  subtitle,
  className,
}: Props) {
  const classes = useStyles()
  const activeDetail = details?.[value]

  return (
    <div className={className}>
      {(label || subtitle) && (
        <div>
          {label && (
            <div className={classes.titleRow}>
              <Text variant="caption1">{label}</Text>
              {tooltip && <HintTooltip message={tooltip} />}
            </div>
          )}
          {subtitle && <Text variant="body2">{subtitle}</Text>}
        </div>
      )}
      <div className={classes.sliderWrapper}>
        <div className={classes.track} aria-hidden="true" />
        <div className={classes.dots} role="group" aria-label={label}>
          {options.map(({ value: optValue, label: optLabel }) => (
            <button
              key={optValue}
              type="button"
              className={classes.dotButton}
              onClick={() => onChange(optValue)}
              aria-label={optLabel}
              aria-pressed={value === optValue}
            >
              <div
                className={`${classes.dot}${value === optValue ? ` ${classes.dotActive}` : ''}`}
              />
              <Text className={value === optValue ? classes.labelActive : ''} variant="body2">
                {optLabel}
              </Text>
            </button>
          ))}
        </div>
      </div>
      {activeDetail && (
        <div className={classes.descriptionBox} data-testid="step-slider-description">
          <Text variant="body2">{activeDetail.description}</Text>
          {activeDetail.timeEstimate && (
            <span className={classes.timeChip}>
              <Text variant="caption1">{activeDetail.timeEstimate}</Text>
            </span>
          )}
        </div>
      )}
    </div>
  )
}
